import React, { useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Modal,
  Fade,
  Backdrop,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import EngineeringIcon from "@mui/icons-material/Engineering";
import VerifiedIcon from "@mui/icons-material/Verified";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ConstructionIcon from "@mui/icons-material/Construction";

import constructionImg from "../../pictures/construction.jpg";

const brands = [
  {
    name: "Saint-Gobain",
    type: "Aluminium Channel",
    image: "https://cdn.simpleicons.org/saint-gobain",
  },
  {
    name: "Saint-Gobain Gyproc",
    type: "Gypsum Board",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Saint-Gobain_logo.svg/512px-Saint-Gobain_logo.svg.png",
  },
  {
    name: "Hafele",
    type: "Hardware & Accessories",
    image:
      "https://www.hafele.com/_Resources/Persistent/6d5e9a6e5c5e4e5e8b0e4e5e8b0e4e5e8b0e4e5e/Hafele_Logo.svg",
  },
  {
    name: "CenturyPly",
    type: "Laminate / Plywood",
    image: "",
  },
  {
    name: "TATA",
    type: "Steel / Aluminium Channel",
    image: "",
  },
  {
    name: "UltraTech",
    type: "Cement",
    image: "",
  },
  {
    name: "ACC",
    type: "Cement",
    image: "",
  },
  {
    name: "Kajaria",
    type: "Tiles",
    image: "",
  },
  {
    name: "Asian Paints",
    type: "Paints",
    image: "",
  },
];
const marqueeBrands = [...brands, ...brands];

const packageData = [
  {
    name: "Basic",
    price: "₹2400/sq.ft",
    features: [
      "Architectural & Structural Design",
      "Quality Vitrified tiles",
      "Simple SS Railing",
      "Standard Emulsion Paint",
    ],
    color: "#2196F3",
  },
  {
    name: "Premium",
    price: "₹2800/sq.ft*",
    features: [
      "Royal Paint & Texture Work",
      "Custom Luxury 3D Elevation",
      "Premium Granite & Texture work",
      "Toughened Glass Railing",
    ],
    color: "#3F51B5",
  },
];

const features = [
  {
    icon: <ConstructionIcon />,
    title: "Traditional Construction",
    subtitle: "Cost Effective",
    desc: "Strong structural foundations with proven traditional construction techniques.",
    detailsType: "packages",
    details: "Rate per sq.ft based on package selection",
  },
  {
    icon: <TrackChangesIcon />,
    title: "100% Transparent",
    subtitle: "Digital Project Tracking",
    desc: "Live updates, photos, and progress monitoring during construction.",
    detailsType: "details",
    details: (
      <Box sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700} mb={3} color="#0d0651">
          Transparent Pricing Structure
        </Typography>

        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={4}>
            <Chip
              label="Basic: ₹399/sq.ft"
              color="primary"
              sx={{
                width: "100%",
                height: 46,
                fontWeight: 600,
                justifyContent: "center",
              }}
            />
            <Typography
              variant="body2"
              mt={1}
              textAlign="center"
              color="#555"
              fontWeight={500}
            >
              Upto 2500 sq.ft
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Chip
              label="Premium: ₹299/sq.ft"
              color="secondary"
              sx={{
                width: "100%",
                height: 46,
                fontWeight: 600,
                justifyContent: "center",
              }}
            />
            <Typography
              variant="body2"
              mt={1}
              textAlign="center"
              color="#555"
              fontWeight={500}
            >
              Upto 5000 sq.ft
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Chip
              label="Premium +: 10-15 %"
              color="warning"
              sx={{
                width: "100%",
                height: 46,
                fontWeight: 600,
                justifyContent: "center",
              }}
            />
            <Typography
              variant="body2"
              mt={1}
              textAlign="center"
              color="#555"
              fontWeight={500}
            >
              Above 5000 sq.ft
            </Typography>
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight={700} mb={2} color="#3a2fb5">
          Benefits You Get
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 3 }}>
          <Chip label="End-to-End Project Management" color="info" />
          <Chip label="Real-time Cost Dashboard" color="success" />
          <Chip label="Weekly Progress Reports" color="primary" />
          <Chip label="100% Transparency" color="error" />
          <Chip label="Live Photo Updates" color="secondary" />
          <Chip label="Quality Check" color="warning" />
          <Chip label="On-Time Delivery" color="success" />
        </Box>

        <Typography variant="body1" color="#555" sx={{ lineHeight: 1.7 }}>
          Complete visibility throughout the project lifecycle with dedicated
          tracking, progress reports, photo updates, and a guarantee
          certificate.
        </Typography>
      </Box>
    ),
  },
  {
    icon: <VerifiedIcon />,
    title: "Quality Standards Checks",
    subtitle: "200+ Quality Checks",
    desc: "High-grade materials from verified vendors with strict quality checks.",
    detailsType: "details",
    details: (
      <Box>
        <Typography variant="h6" fontWeight={700} mb={2} color="#0d0651">
          Material Quality Standards
        </Typography>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ mt: 2, borderRadius: 3, overflow: "hidden" }}
        >
          <Table size="small">
            <TableHead
              sx={{
                background: "linear-gradient(90deg, #0d0651, #3a2fb5)",
              }}
            >
              <TableRow>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                  Material
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#fff", fontWeight: 700 }}
                >
                  Quality Standard
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell>Structural Steel</TableCell>
                <TableCell align="right">FE 500/550 D</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Cement</TableCell>
                <TableCell align="right">33/53 Grade</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tiles/Flooring</TableCell>
                <TableCell align="right">KAG / Anuj</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Electrical Wiring</TableCell>
                <TableCell align="right">Anchor</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Plumbing Pipes</TableCell>
                <TableCell align="right">Finolex</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="body1" mt={2} color="#555">
          All materials are sourced from authorized dealers with third-party
          quality certification.
        </Typography>
      </Box>
    ),
  },
  {
    icon: <EngineeringIcon />,
    title: "Skilled Team",
    subtitle: "Experienced Professionals",
    desc: "Expert civil engineers and trained workers ensuring precision execution.",
    detailsType: "details",
    details: (
      <Box>
        <Typography variant="h6" fontWeight={700} mb={2} color="#0d0651">
          Our Professional Team
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
          <Chip label="10+ Years Industry Experience" color="success" />
          <Chip label="50+ Projects Delivered" color="info" />
          <Chip label="Site Supervisor" color="warning" />
          <Chip label="24/7 Live Surveillance" color="primary" />
          <Chip label="Architect Designers" color="secondary" />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" fontWeight={700} mb={1} color="#3a2fb5">
          Dedicated Project Roles
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body1">
            <strong>Project Manager:</strong> Single point of contact for the
            client
          </Typography>
          <Typography variant="body1">
            <strong>Site Supervisor:</strong> Daily on-site supervision
          </Typography>
          <Typography variant="body1">
            <strong>Project Engineer:</strong> Quality inspection and execution
            checks
          </Typography>
        </Box>
      </Box>
    ),
  },
];

function ConstructionService() {
  const [openModal, setOpenModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const closeTimer = useRef(null);

  const openFeature = (feature) => {
    clearTimeout(closeTimer.current);
    const data =
      feature.detailsType === "packages"
        ? { ...feature, packages: packageData }
        : feature;

    setSelectedFeature(data);
    setOpenModal(true);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => {
      setOpenModal(false);
      setSelectedFeature(null);
    }, 150);
  };

  const closeModal = () => {
    clearTimeout(closeTimer.current);
    setOpenModal(false);
    setSelectedFeature(null);
  };

  const handleMove = (e) => {
    if (window.innerWidth < 900) return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rotateY = (x / rect.width - 0.5) * 16;
    const rotateX = -(y / rect.height - 0.5) * 16;

    card.style.setProperty("--rx", `${rotateX}deg`);
    card.style.setProperty("--ry", `${rotateY}deg`);
  };

  const resetMove = (e) => {
    e.currentTarget.style.setProperty("--rx", "0deg");
    e.currentTarget.style.setProperty("--ry", "0deg");
  };

  return (
    <Box sx={{ background: "#fff" }}>
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 320, md: 420 },
          background: `linear-gradient(rgba(13,6,81,0.68), rgba(13,6,81,0.88)), url(${constructionImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        {/* floating background glow */}
        <Box
          sx={{
            position: "absolute",
            width: 240,
            height: 240,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.20), rgba(255,255,255,0.02))",
            top: "8%",
            left: "8%",
            filter: "blur(8px)",
            animation: "floatGlow 8s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(79,109,255,0.35), rgba(79,109,255,0.04))",
            bottom: "10%",
            right: "10%",
            filter: "blur(10px)",
            animation: "floatGlow 10s ease-in-out infinite 1s",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            px: 4,
            py: 4,
            borderRadius: 4,
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.14)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.24)",
            animation: "heroPanelFloat 6s ease-in-out infinite",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "2rem", md: "3.2rem" },
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Construction Services
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
              color: "rgba(255,255,255,0.88)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.95rem", md: "1.05rem" },
            }}
          >
            Building your dream space with precision and care. From foundation
            to finishing, we ensure quality construction that stands the test of
            time.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          py: 12,
          background:
            "linear-gradient(135deg, #f7f8ff 0%, #edf2ff 45%, #e6ecff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 10% 20%, rgba(58,47,181,0.08), transparent 28%), radial-gradient(circle at 90% 80%, rgba(245,124,0,0.08), transparent 25%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight={800}
            mb={10}
            sx={{
              background: "linear-gradient(135deg, #0d0651 0%, #3a2fb5 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: 1.2,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Why Choose Our Construction
          </Typography>

          <Grid container spacing={5}>
            {features.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} display="flex">
                <Card
                  onClick={() => openFeature(item)}
                  onMouseEnter={() => {
                    setHoveredIndex(index);
                    openFeature(item);
                  }}
                  onMouseMove={handleMove}
                  onMouseLeave={(e) => {
                    setHoveredIndex(null);
                    resetMove(e);
                    scheduleClose();
                  }}
                  sx={{
                    "--rx": "0deg",
                    "--ry": "0deg",
                    width: "100%",
                    borderRadius: 5,
                    p: 0,
                    overflow: "visible",
                    background: "transparent",
                    boxShadow: "none",
                    perspective: "1600px",
                    cursor: "pointer",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -8,
                      borderRadius: 6,
                      background:
                        "linear-gradient(145deg, rgba(58,47,181,0.28), rgba(245,124,0,0.14))",
                      filter: "blur(18px)",
                      opacity: hoveredIndex === index ? 1 : 0.45,
                      transition: "all 0.35s ease",
                    },
                  }}
                >
                  <Box
                    sx={{
                      minHeight: 340,
                      height: "100%",
                      borderRadius: 5,
                      p: 4,
                      position: "relative",
                      background:
                        "linear-gradient(145deg, rgba(255,255,255,0.92), rgba(243,246,255,0.78))",
                      border: "1px solid rgba(255,255,255,0.65)",
                      backdropFilter: "blur(16px)",
                      boxShadow: "0 18px 45px rgba(13,6,81,0.12)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transformStyle: "preserve-3d",
                      transform:
                        hoveredIndex === index
                          ? "translateY(-14px) scale(1.03) rotateX(var(--rx)) rotateY(var(--ry))"
                          : "translateY(0px) scale(1) rotateX(0deg) rotateY(0deg)",
                      transition:
                        "transform 0.18s ease, box-shadow 0.32s ease, background 0.32s ease",
                      "&:hover": {
                        boxShadow: "0 28px 70px rgba(13,6,81,0.2)",
                        background:
                          "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(235,241,255,0.86))",
                      },
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: "22px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(145deg, #0d0651 0%, #3a2fb5 100%)",
                          color: "#fff",
                          mb: 3,
                          boxShadow: "0 14px 28px rgba(13,6,81,0.25)",
                          transform: "translateZ(34px)",
                        }}
                      >
                        <Box sx={{ fontSize: 34, display: "flex" }}>
                          {item.icon}
                        </Box>
                      </Box>

                      <Typography
                        variant="h5"
                        fontWeight={800}
                        mb={1}
                        sx={{
                          fontSize: { xs: "1.3rem", md: "1.5rem" },
                          lineHeight: 1.3,
                          color: "#0d0651",
                          fontFamily: "'Roboto Slab', serif",
                          transform: "translateZ(24px)",
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        mb={2}
                        sx={{
                          color: "#3a2fb5",
                          fontSize: "1rem",
                          transform: "translateZ(18px)",
                        }}
                      >
                        {item.subtitle}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: "#555",
                          lineHeight: 1.7,
                          fontSize: "0.98rem",
                          transform: "translateZ(12px)",
                        }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Modal
        open={openModal}
        onClose={closeModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={openModal}>
          <Box
            onMouseEnter={() => clearTimeout(closeTimer.current)}
            onMouseLeave={scheduleClose}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "94%", sm: "88%", md: 720 },
              maxHeight: "92vh",
              overflowY: "auto",
              borderRadius: 5,
              p: { xs: 3, md: 4 },
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(242,245,255,0.96))",
              boxShadow: "0 30px 80px rgba(0,0,0,0.25)",
              border: "1px solid rgba(255,255,255,0.7)",
              outline: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                gap: 2,
              }}
            >
              <Typography variant="h5" fontWeight={800} color="#0d0651">
                {selectedFeature?.title}
              </Typography>

              <Button
                onClick={closeModal}
                sx={{
                  minWidth: 42,
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  color: "#0d0651",
                }}
              >
                <CloseIcon />
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {selectedFeature?.detailsType === "packages" ? (
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  mb={3}
                  color="#3a2fb5"
                >
                  Choose Your Package
                </Typography>

                <Grid container spacing={3}>
                  {selectedFeature?.packages?.map((pkg, idx) => (
                    <Grid item xs={12} md={6} key={idx}>
                      <Card
                        sx={{
                          borderRadius: 4,
                          background: `linear-gradient(145deg, ${pkg.color}16, #ffffff)`,
                          border: `1px solid ${pkg.color}33`,
                          boxShadow: "0 14px 30px rgba(13,6,81,0.08)",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: "0 18px 34px rgba(13,6,81,0.16)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3.2, textAlign: "center" }}>
                          <Chip
                            label={pkg.name}
                            sx={{
                              mb: 2,
                              px: 1.5,
                              height: 42,
                              fontWeight: 800,
                              color: "#fff",
                              background: pkg.color,
                            }}
                          />

                          <Typography
                            variant="h4"
                            fontWeight={800}
                            color="#0d0651"
                            mb={2}
                          >
                            {pkg.price}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.2,
                              alignItems: "flex-start",
                            }}
                          >
                            {pkg.features.map((feature, fIdx) => (
                              <Box
                                key={fIdx}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: pkg.color,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="body2" color="#444">
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              selectedFeature?.details
            )}
          </Box>
        </Fade>
      </Modal>

      <Box
        sx={{
          py: 12,
          background: "linear-gradient(180deg, #fffdf9 0%, #f8f9ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight={800}
            mb={4}
            sx={{
              color: "#0d0651",
              fontFamily: "'Roboto Slab', serif",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Some of the Brands that we use
          </Typography>
          <Box
            sx={{
              "@keyframes brandMarquee": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-50%)" },
              },
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: "100%",
              overflow: "hidden",
              px: { xs: 2, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                width: "max-content",
                alignItems: "center",
                gap: 2.5,
                animation: "brandMarquee 22s linear infinite",
                willChange: "transform",
                "&:hover": {
                  animationPlayState: "paused",
                },
              }}
            >
              {marqueeBrands.map((brand, index) => (
                <Box
                  key={`${brand.name}-${index}`}
                  sx={{
                    flex: "0 0 auto",
                    width: 220,
                    py: 3,
                    px: 2.5,
                    borderRadius: 4,
                    background: "#fff",
                    border: "1px solid rgba(13,6,81,0.07)",
                    boxShadow: "0 14px 34px rgba(13,6,81,0.08)",
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: 70,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {brand.image ? (
                      <Box
                        component="img"
                        src={brand.image}
                        alt={brand.name}
                        sx={{
                          maxWidth: "90%",
                          maxHeight: 60,
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "1.55rem",
                          fontWeight: 800,
                          color: "#111",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {brand.name}
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: "#0d0651",
                      fontSize: "1rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {brand.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default ConstructionService;
